import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Loader2, Printer, ShieldX } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { api, apiError } from "../lib/api";

const PRINT_ROLES = ["admin", "welcome_coordinator"];

const usePrintAuth = () => {
  const [state, setState] = useState({ user: null, checking: true, denied: false });
  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) { setState({ user: null, checking: false, denied: false }); return; }
    api.get("/auth/me")
      .then((r) => setState({ user: r.data, checking: false, denied: !PRINT_ROLES.includes(r.data.role) }))
      .catch(() => { localStorage.removeItem("hz_admin_token"); setState({ user: null, checking: false, denied: false }); });
  }, []);
  return state;
};

const PrintShell = ({ title, filters, columns, rows, summary, children, testId }) => (
  <div className="min-h-screen bg-white p-6 print:p-0" data-testid={testId}>
    <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between gap-4 border-b-2 border-hz-navy pb-4">
        <Logo variant="full" className="h-14" />
        <div className="text-right">
          <p className="text-sm font-extrabold text-hz-navy">{title}</p>
          <p className="text-xs text-hz-ink/60">Generado: {new Date().toLocaleString("es-MX")}</p>
          {filters && <p className="text-xs text-hz-ink/60">Filtros: {filters}</p>}
        </div>
      </div>
      {summary && (
        <div className="mt-4 flex flex-wrap gap-4 rounded-xl bg-hz-bg px-4 py-3">
          {summary.map(([k, v]) => (
            <p key={k} className="text-xs"><span className="font-semibold text-hz-ink/60">{k}:</span> <span className="font-extrabold text-hz-navy">{v}</span></p>
          ))}
        </div>
      )}
      {children}
      {columns && (
        <table className="mt-4 w-full border-collapse text-xs">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c} className="border border-hz-gray/60 bg-hz-navy px-2 py-1.5 text-left font-bold text-white">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="border border-hz-gray/40 px-3 py-4 text-center text-hz-ink/50">Sin registros</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-hz-bg" : ""}>
                {r.map((cell, j) => (
                  <td key={j} className="border border-hz-gray/40 px-2 py-1.5">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-6 print:hidden">
        <Button onClick={() => window.print()} data-testid="print-action-btn" className="bg-hz-navy font-bold text-white hover:bg-hz-blue">
          <Printer className="mr-2 h-4 w-4" /> Imprimir / Guardar PDF
        </Button>
      </div>
      <p className="mt-6 text-center text-[10px] font-semibold uppercase tracking-widest text-hz-ink/40">
        Horizonte 2026 — Liderazgo con Visión Reformada
      </p>
    </div>
  </div>
);

const usePrintReport = (reportType, params = {}) => {
  const auth = usePrintAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!auth.user || auth.denied) return;
    api.get(`/admin/reports/${reportType}`, { params: { ...params, format: "json" } })
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, auth.denied, reportType]);
  return { ...auth, data, error };
};

const Guard = ({ auth, children }) => {
  if (auth.checking) return <div className="flex min-h-screen items-center justify-center text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!auth.user) return <Navigate to="/login?redirect=/admin" replace />;
  if (auth.denied) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
        <ShieldX className="h-10 w-10 text-hz-error" />
        <p className="font-bold text-hz-navy" data-testid="print-denied">Tu rol no tiene acceso a vistas imprimibles.</p>
      </div>
    );
  }
  return children;
};

export const PrintGeneral = () => {
  const auth = usePrintReport("evento-general");
  return (
    <Guard auth={auth}>
      {!auth.data ? <div className="flex min-h-screen items-center justify-center text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
        <PrintShell title="Lista de asistentes autorizados — Evento general" columns={auth.data.columns} rows={auth.data.rows} summary={auth.data.summary} testId="print-general" />
      )}
    </Guard>
  );
};

export const PrintCena = () => {
  const auth = usePrintReport("cena");
  return (
    <Guard auth={auth}>
      {!auth.data ? <div className="flex min-h-screen items-center justify-center text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
        <PrintShell title="Lista de asistentes autorizados — Cena especial" columns={auth.data.columns} rows={auth.data.rows} summary={auth.data.summary} testId="print-cena" />
      )}
    </Guard>
  );
};

export const PrintTaller = () => {
  const { workshopId } = useParams();
  const auth = usePrintReport("talleres", { workshop_id: workshopId });
  return (
    <Guard auth={auth}>
      {!auth.data ? <div className="flex min-h-screen items-center justify-center text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
        <PrintShell title="Lista de asistentes por taller" columns={auth.data.columns} rows={auth.data.rows} summary={auth.data.summary} testId="print-taller" />
      )}
    </Guard>
  );
};

export const PrintResumen = () => {
  const auth = usePrintAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!auth.user || auth.denied) return;
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, [auth.user, auth.denied]);
  return (
    <Guard auth={auth}>
      {!data ? <div className="flex min-h-screen items-center justify-center text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
        <PrintShell title="Resumen ejecutivo de asistencia" testId="print-resumen"
          summary={Object.entries({
            "Pre-registrados": data.totals.total_registered,
            "Pago aprobado": data.totals.approved,
            "Comprobantes en revisión": data.totals.pending_review,
            "Pagos rechazados": data.totals.rejected,
            "Cortesías activas": data.totals.courtesies,
            "Autorizados cena": data.totals.dinner_authorized,
            "Ingresaron a cena": data.totals.dinner_checked,
            "Autorizados evento general": data.totals.general_authorized,
            "Ingresaron al evento": data.totals.general_checked,
            "Asistencia evento general": `${data.totals.general_attendance_pct}%`,
            "Registros en sitio": data.totals.onsite,
            "Check-ins de hoy": data.totals.checkins_today,
            "Incidencias abiertas": data.totals.incidents_open,
          })}>
          <table className="mt-4 w-full border-collapse text-xs">
            <thead>
              <tr>
                {["Taller", "Estado", "Inscritos", "Presentes", "Asistencia", "Disponibles"].map((c) => (
                  <th key={c} className="border border-hz-gray/60 bg-hz-navy px-2 py-1.5 text-left font-bold text-white">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.workshops.map((w) => (
                <tr key={w.id}>
                  <td className="border border-hz-gray/40 px-2 py-1.5 font-semibold">{w.name}</td>
                  <td className="border border-hz-gray/40 px-2 py-1.5">{w.state}</td>
                  <td className="border border-hz-gray/40 px-2 py-1.5">{w.registered_count}</td>
                  <td className="border border-hz-gray/40 px-2 py-1.5">{w.present_count}</td>
                  <td className="border border-hz-gray/40 px-2 py-1.5">{w.attendance_pct}%</td>
                  <td className="border border-hz-gray/40 px-2 py-1.5">{w.available}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PrintShell>
      )}
    </Guard>
  );
};
